/* =====================================================================
 * Tenant-scoped browser storage helpers.
 *
 * Rationale:
 *   Every piece of persistent state the web apps keep in `localStorage`
 *   / `sessionStorage` / IndexedDB (`DB_NAME`) needs a tenant-specific
 *   prefix so two tenants served from the same origin (e.g. B2C preview
 *   and a sample on-prem build running side-by-side in a developer
 *   browser) do not collide on "wallpaper", "user_cache", "widget
 *   canvas", "RQ cache", "dm outbox", etc.
 *
 *   The rule is intentionally trivial: `${tenant.id}_`. B2C (tenant.id
 *   === 'askyourai') therefore keeps the historic `askyourai_` prefix
 *   verbatim; Samsung → `samsung_`, Sungwoo → `sungwoo_`. Keep the
 *   helper pure (takes a `TenantManifest`, never reads `window`) so
 *   callers decide when/where to resolve the tenant (React context,
 *   `getTenantSnapshot()` from `@auw/onprem`, or a build-time import).
 *
 *   Note: no migration logic lives here. Pre-launch, there are no
 *   users to migrate; post-launch tenants own their own origin so a
 *   bare `${id}_` prefix never meets stale data from a different
 *   tenant. If we ever need migration we add it explicitly instead of
 *   smuggling it into the prefix generator.
 * ===================================================================== */

import type { TenantManifest } from './types';

/**
 * Returns the storage prefix every app-level key should be namespaced
 * with. Trailing underscore is included so callers write the natural
 * `${prefix}user_cache` concatenation without a separate separator.
 *
 * Example:
 *   tenantStoragePrefix(b2cManifest)     // 'askyourai_'
 *   tenantStoragePrefix(samsungManifest) // 'samsung_'
 */
export function tenantStoragePrefix(tenant: TenantManifest): string {
  return `${tenant.id}_`;
}

/**
 * Convenience: compose a fully-qualified storage key from the tenant
 * prefix and an un-prefixed suffix. Keeps call-sites readable
 * (`tenantStorageKey(tenant, 'user_cache')` vs string-concat).
 */
export function tenantStorageKey(
  tenant: TenantManifest,
  suffix: string,
): string {
  return `${tenantStoragePrefix(tenant)}${suffix}`;
}
